import { useMutation } from "@tanstack/react-query";
import { decamelizeKeys } from "humps";
import { useBookingInvalidate } from "PFCore/hooks/queries";
import { ActivitiesCollection } from "PFCore/hooks/queries/activities/query_keys";
import { useActivitiesInvalidate } from "PFCore/hooks/queries/activities/use_activities_invalidate";
import { useBookingsForUpdateInvalidate } from "PFCore/hooks/queries/bookings/bulk_operation/use_bookings_for_update_invalidate";
import { useEngagementsInvalidate } from "PFCore/hooks/queries/engagements/use_engagements_invalidate";
import { useProfileInvalidate } from "PFCore/hooks/queries/profile";
import { useRolesInvalidate } from "PFCore/hooks/queries/roles/use_roles_invalidate";
import { createOwner, OwnerCreatePayload } from "PFCore/services/owners";
import { Owner } from "PFTypes";
import { MutationOptions } from "PFTypes/request_options";

import { useActivityInvalidate } from "../activity/use_activity_invalidate";

export const useOwnerCreate = ({ onSuccess, ...options }: MutationOptions<OwnerCreatePayload> = {}) => {
  const { invalidate: invalidateActivity } = useActivityInvalidate();
  const { invalidateByCollection: invalidateActvitiesByCollection } = useActivitiesInvalidate();
  const { invalidateRoles } = useRolesInvalidate();
  const { invalidateAll: invalidateEngagements } = useEngagementsInvalidate();
  const { invalidate: invalidateForUpdate } = useBookingsForUpdateInvalidate();
  const { invalidateAllProfiles } = useProfileInvalidate();
  const { invalidate: invalidateBookings } = useBookingInvalidate();

  return useMutation({
    mutationFn: (payload: OwnerCreatePayload) => createOwner(payload),
    onSuccess: (data, ...params) => {
      const owner = decamelizeKeys(data) as Owner;
      if (owner.activity_id) {
        invalidateActivity([owner.activity_id]);
        invalidateRoles();
        invalidateEngagements();
      }
      invalidateActvitiesByCollection(ActivitiesCollection.All);
      invalidateForUpdate();
      invalidateAllProfiles();
      invalidateBookings();
      onSuccess?.(owner, ...params);
    },
    ...options
  });
};
